import axios from 'axios';
import type { BillingUpdateResultDto } from './paymentsService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

// סוג הנחה (רשימה קבועה - תואם enum DiscountType בשרת)
export enum DiscountType {
  OneTime = 1,
  Monthly = 2,
}

export const DiscountTypeOptions: { value: DiscountType; label: string }[] = [
  { value: DiscountType.OneTime, label: 'חד-פעמי' },
  { value: DiscountType.Monthly, label: 'חודשי' },
];

// Types
// ⚠️ סכומי ההנחה הם נטו — לפני מע"מ. ההנחה מפחיתה את העמלה שמחייבים, ולכן המע"מ עליה
// כלל אינו נגבה: הנחה של 20,000 ₪ מורידה מהחוב 23,600 ₪. הגירוס מתבצע בשרת.
export interface DiscountDto {
  id: number;
  clientId: number;
  clientName: string;
  amount: number; // נטו, לפני מע"מ. לחודשי — הסכום לחודש
  discountDate: string;
  type: number;
  typeName: string;
  endDate?: string | null;
  notes?: string;
  effectiveAmount: number; // ההנחה שהוחלה בפועל (אחרי תקרה) — נטו, לפני מע"מ. סכום כל ההנחות = totalDiscount
  createdAt: string;
  updatedAt: string;
}

// תשובת שמירת הנחה: ההנחה ומה קרה לתוכנית התשלומים הפעילה (null — אין ללקוח חיוב)
export interface DiscountSaveResultDto {
  discount: DiscountDto;
  planSync: BillingUpdateResultDto | null;
}

export interface CreateDiscountDto {
  clientId: number;
  amount: number; // נטו, לפני מע"מ
  discountDate: string;
  type: number;
  endDate?: string | null;
  notes?: string;
}

export interface UpdateDiscountDto {
  amount: number; // נטו, לפני מע"מ
  discountDate: string;
  type: number;
  endDate?: string | null;
  notes?: string;
}

export interface DiscountFilter {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  type?: number;
}

export interface DiscountPagedResult {
  items: DiscountDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface DiscountSummaryDto {
  totalDiscounts: number;
  clientsWithDiscountsCount: number;
  monthlyDiscountsCount: number;
  oneTimeDiscountsCount: number;
}

const BASE = `${API_BASE_URL}/discounts`;

export const discountsApi = {
  // Per-client
  getByClient: async (clientId: number): Promise<DiscountDto[]> => {
    const response = await axios.get<DiscountDto[]>(`${BASE}/client/${clientId}`);
    return response.data;
  },

  create: async (dto: CreateDiscountDto): Promise<DiscountSaveResultDto> => {
    const response = await axios.post<DiscountSaveResultDto>(BASE, dto);
    return response.data;
  },

  update: async (id: number, dto: UpdateDiscountDto): Promise<DiscountSaveResultDto> => {
    const response = await axios.put<DiscountSaveResultDto>(`${BASE}/${id}`, dto);
    return response.data;
  },

  /** מחזיר מה קרה לתוכנית התשלומים הפעילה אחרי המחיקה; null כשאין ללקוח חיוב */
  remove: async (id: number): Promise<BillingUpdateResultDto | null> => {
    const response = await axios.delete<BillingUpdateResultDto | ''>(`${BASE}/${id}`);
    return response.data || null;
  },

  // Dashboard (מנהל מערכת)
  listAll: async (filter: DiscountFilter): Promise<DiscountPagedResult> => {
    const response = await axios.get<DiscountPagedResult>(BASE, { params: filter });
    return response.data;
  },

  getSummary: async (): Promise<DiscountSummaryDto> => {
    const response = await axios.get<DiscountSummaryDto>(`${BASE}/summary`);
    return response.data;
  },
};

// Helpers (פורמט he-IL, תואם שאר השירותים)
export const formatCurrency = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) return '-';
  return `₪${amount.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
